import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Switch,
  CircularProgress,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import EmailIcon from '@mui/icons-material/Email';
import { useTheme } from '@mui/material/styles';
import { useAuth0 } from '@auth0/auth0-react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContactFormModal from '../forms/ContactFormModal';
import { openCustomerPortal } from '../../utils/billing';
import { getEligibleManualResearchGrant } from '../../utils/subscriptionEntitlements';
export default function UserMenu({ toggleTheme }) {
  const { user, isAuthenticated, isLoading,
          logout, getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const open  = Boolean(anchorEl);

  if (isLoading) return <CircularProgress size={24} />;   // while Auth0 loads
  if (!isAuthenticated) return null;                      // nothing for guests

  const subStatus =
    user?.['https://softwareiq.io/subscriptionStatus'] ?? 'free';
  const showManageBtn = ['trialing', 'active', 'past_due'].includes(subStatus);
  const manualGrant = getEligibleManualResearchGrant(user);
  const coverageLabel = manualGrant?.kind === 'trial' ? 'Research trial'
    : manualGrant?.kind === 'enterprise' ? `Research · ${manualGrant.contractName || manualGrant.contractId}` : null;

  const handlePortal = async () => {
    try {
      setAnchorEl(null);
      await openCustomerPortal(getAccessTokenSilently);
    } catch (err) {
      console.error(err);
      window.alert(err.message || 'Unable to open billing portal – please try again.');
    }
  };
    const isDark     = theme.palette.mode === 'dark';   // ← current mode
  

    const handleSwitchChange = () => toggleTheme();

  return (
    <Box sx={{ borderRadius: 1, display: 'flex', alignItems: 'center' }}>
      {/* avatar button */}
      <IconButton
        onClick={e => setAnchorEl(e.currentTarget)}
        size="small"
        sx={{ ml: 0 }}
      >
        <AccountCircleIcon
          sx={{
            width: 26,
            height: 26,
            color: theme.palette.primary.main,
          }}
        />
      </IconButton>

      {/* dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{

          elevation: 3,
          sx: { backgroundColor:theme.background.transparent,minWidth: 200, backdropFilter: 'blur(3px)' },
        }}
      >
        <MenuItem sx={{color:theme.palette.text.secondary}} >
          <ListItemText
            primary={"Welcome"}
            secondary={[user?.email, coverageLabel].filter(Boolean).join(' · ')}
            primaryTypographyProps={{ fontWeight: 'bold' }}
            sx={{ pr: 1,color:theme.palette.text.secondary }}
          />
        </MenuItem>

        <Divider />
         <MenuItem
          onClick={toggleTheme}          /* whole row toggles */
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            color: theme.palette.text.secondary,
            cursor: 'pointer',
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Brightness4Icon sx={{ color: theme.palette.primary.main }} />
          </ListItemIcon>

          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {isDark ? 'Dark Mode' : 'Light Mode'}
          </Typography>

          {/* compact switch with inline sx */}
          <Switch
            checked={!isDark}
            onChange={handleSwitchChange}
            onClick={e => e.stopPropagation()}   /* prevent double-toggle */
            inputProps={{ 'aria-label': 'theme toggle' }}
            sx={{

            }}
          />
        </MenuItem>
        <Divider />

        {showManageBtn && (
          <MenuItem sx={{color:theme.palette.text.secondary}} onClick={handlePortal}>
            <ListItemIcon>
              <CreditCardIcon sx={{color:theme.palette.text.secondary}} fontSize="small" />
            </ListItemIcon>
            Manage subscription
          </MenuItem>
        )}

        <MenuItem
          sx={{color:theme.palette.text.secondary}}
          onClick={() => {
            setAnchorEl(null);
            setContactModalOpen(true);
          }}
        >
          <ListItemIcon>
            <EmailIcon sx={{color:theme.palette.text.secondary}} fontSize="small" />
          </ListItemIcon>
          Contact Us
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() =>
            logout({ logoutParams: { returnTo: window.location.origin } })
          }
          sx={{color:theme.palette.text.secondary}} 
        >
          <ListItemIcon>
            <LogoutIcon sx={{color:theme.palette.text.secondary}}  fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      {/* Contact Form Modal */}
      <ContactFormModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
      />
    </Box>
  );
}
